<template>
  <div>
    <v-sheet elevation="1">
      <v-toolbar dense color="white">
        <v-toolbar-title>{{ screenTitle }}</v-toolbar-title>
        <v-spacer></v-spacer>
        <!-- ダウンロードボタン -->
        <v-btn
          v-if="!readOnly"
          class="mx-2"
          color="primary"
          @click="handleDownload"
        >
          ダウンロード
        </v-btn>
        <!-- 編集ダイアログ -->
        <v-dialog v-model="dialog" persistent>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              v-if="!readOnly"
              v-bind="attrs"
              v-on="on"
              class="mx-2"
              color="primary"
            >
              「新規登録」はこのボタンを押してください
            </v-btn>
          </template>
          <v-card>
            <v-form ref="form" v-model="valid">
              <v-card-title class="pb-0">
                <span class="headline">{{
                  $formTitle("自治会マスタ", readOnly, isNewEditedItem)
                }}</span>
              </v-card-title>
              <v-card-text>
                <v-container>
                  <p class="red--text mb-0">※：必須項目です</p>
                  <v-row v-if="isEditable('autonomy_name')">
                    <v-col cols="12">
                      <!-- 自治会名 -->
                      <v-text-field
                        v-model="editedItem.autonomy_name"
                        label="自治会名 ※"
                        :rules="readOnly ? [] : [rules.required()]"
                        v-bind="formControlProps(config.PLACEHOLDER_INPUT, 50)"
                      >
                      </v-text-field>
                    </v-col>
                  </v-row>
                  <v-row v-if="isEditable('autonomy_name_ruby')">
                    <v-col cols="12">
                      <!-- 自治会名（カナ） -->
                      <v-text-field
                        v-model="editedItem.autonomy_name_ruby"
                        label="自治会名（カナ） ※"
                        :rules="readOnly ? [] : [rules.required()]"
                        v-bind="formControlProps(config.PLACEHOLDER_INPUT, 50)"
                      >
                      </v-text-field>
                    </v-col>
                  </v-row>
                  <v-row
                    v-if="
                      isEditable('population') && isEditable('households_count')
                    "
                  >
                    <v-col cols="3">
                      <!-- 人口 -->
                      <v-text-field
                        :value="editedItem.population"
                        @input="editedItem.population = $event ? $event : ''"
                        label="人口 ※"
                        suffix="人"
                        :rules="
                          readOnly ? [] : [rules.required(), rules.numeric()]
                        "
                        v-bind="formControlProps(config.PLACEHOLDER_INPUT, 4)"
                      >
                      </v-text-field>
                    </v-col>
                    <v-col cols="3">
                      <!-- 世帯数 -->
                      <v-text-field
                        :value="editedItem.households_count"
                        @input="
                          editedItem.households_count = $event ? $event : ''
                        "
                        label="世帯数 ※"
                        suffix="世帯"
                        :rules="
                          readOnly ? [] : [rules.required(), rules.numeric()]
                        "
                        v-bind="formControlProps(config.PLACEHOLDER_INPUT, 4)"
                      >
                      </v-text-field>
                    </v-col>
                  </v-row>
                  <v-row v-if="isEditable('municip_code')">
                    <v-col cols="3">
                      <!-- 市町村 -->
                      <v-select
                        v-model="editedItem.municip_code"
                        :items="municipalities"
                        label="市町村 ※"
                        :rules="readOnly ? [] : [rules.required()]"
                        v-bind="formControlProps(config.PLACEHOLDER_SELECT)"
                      ></v-select>
                    </v-col>
                    <v-col cols="3">
                      <!-- 地域 -->
                      <v-select
                        v-model="editedItem.area_code"
                        :items="$facility.areasForSearch(editedareas)"
                        label="地域（地区） ※"
                        :rules="readOnly ? [] : [rules.required()]"
                        v-bind="formControlProps(config.PLACEHOLDER_SELECT)"
                      ></v-select>
                    </v-col>
                  </v-row>
                  <v-row v-if="isEditable('meeting_info')">
                    <v-col cols="12">
                      <meeting-info
                        :meeting-info="editedItem.meeting_info"
                        :municipalities="municipalities"
                        :facilities="facilities_meeting"
                        :read-only="this.readOnly"
                        :autonomy-cd="editedItem.autonomy_code"
                        :edited-meeting-info.sync="
                          editedItem.edited_meeting_info
                        "
                        ref="meetingInfo"
                      />
                    </v-col>
                  </v-row>
                  <v-row v-if="isEditable('shelter_info')">
                    <v-col cols="12">
                      <shelter-info
                        :shelter-info="editedItem.shelter_info"
                        :municipalities="municipalities"
                        :facilities="facilities_shelter"
                        :read-only="this.readOnly"
                        :autonomy-cd="editedItem.autonomy_code"
                        :edited-shelter-info.sync="
                          editedItem.edited_shelter_info
                        "
                        ref="shelterInfo"
                      />
                    </v-col>
                  </v-row>
                </v-container>
              </v-card-text>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                  v-if="!readOnly"
                  class="mx-2"
                  color="primary"
                  :loading="loading"
                  :disabled="!valid"
                  @click="handleSaveEditDialog"
                >
                  {{ isNewEditedItem ? "登録" : "更新" }}
                </v-btn>
                <v-btn
                  class="mx-2"
                  color="primary"
                  :disabled="loading"
                  @click="handleCloseEditDialog"
                >
                  {{ $cancelButtonText(readOnly) }}
                </v-btn>
              </v-card-actions>
            </v-form>
          </v-card>
        </v-dialog>
      </v-toolbar>
      <v-container fluid>
        <error-message :error-object="errorObject" />
        <success-message :success-object="successObject" />
        <p style="margin: 5px 0 15px 0;">
          自治会マスタは下の表を参照してください。条件を指定することで一覧から絞り込むことができます。
        </p>
        <v-row>
          <v-col cols="6" md="3">
            <!-- 検索項目：市町村 -->
            <v-select
              v-model="searchItem.municipality"
              :items="municipalitiesAddAll"
              label="市町村"
              :placeholder="config.PLACEHOLDER_SELECT"
              outlined
              hide-details
            >
            </v-select>
          </v-col>
          <v-col cols="6" md="3">
            <!-- 検索項目：地域（地区） -->
            <v-select
              v-model="searchItem.area"
              :items="$facility.areasForSearch(areas)"
              label="地域（地区）"
              :placeholder="config.PLACEHOLDER_SELECT"
              clearable
              outlined
              hide-details
            ></v-select>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="6" md="2">
            <!-- 検索項目：自治会等人口 -->
            <v-text-field
              v-model="searchItem.population_low"
              :items="$facility.tempFacilityFlags"
              label="人口（最小）"
              :placeholder="config.PLACEHOLDER_SP"
              suffix="人"
              outlined
              hide-details
            >
            </v-text-field>
          </v-col>
          <v-col cols="6" md="2">
            <!-- 検索項目：自治会等人口 -->
            <v-text-field
              v-model="searchItem.population_high"
              :items="$facility.tempFacilityFlags"
              label="人口（最大）"
              :placeholder="config.PLACEHOLDER_SP"
              suffix="人"
              outlined
              hide-details
            >
            </v-text-field>
          </v-col>
          <v-col cols="6" md="2">
            <!-- 検索項目：世帯数 -->
            <v-text-field
              v-model="searchItem.households_count_low"
              :items="$facility.tempFacilityFlags"
              label="世帯数（最小）"
              :placeholder="config.PLACEHOLDER_SP"
              suffix="世帯"
              outlined
              hide-details
            >
            </v-text-field>
          </v-col>
          <v-col cols="6" md="2">
            <!-- 検索項目：世帯数 -->
            <v-text-field
              v-model="searchItem.households_count_high"
              :items="$facility.tempFacilityFlags"
              label="世帯数（最大）"
              :placeholder="config.PLACEHOLDER_SP"
              suffix="世帯"
              outlined
              hide-details
            >
            </v-text-field>
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <p class="grey--text">
              部分一致で検索することができます。<br />
              検索は自治会名、自治会名（カナ）の項目が対象です。<br />
              スペースを挟み、複数の語句で検索することはできません。<br />
            </p>
          </v-col>
          <v-col cols="12">
            <!-- 検索項目：フリーテキスト -->
            <v-text-field
              v-model="searchItem.freeText"
              label="フリーテキスト"
              placeholder="自治会名、自治会名（カナ）から検索"
              clearable
              append-icon="mdi-magnify"
              outlined
              hide-details
            >
            </v-text-field>
          </v-col>
        </v-row>
        <v-divider class="mb-1"></v-divider>
      </v-container>
      <!-- 一覧表 -->
      <top-operation-data-table
        ref="table"
        v-if="showsDataTable"
        :headers="headers"
        :items="filteredAutonomys"
        locale="ja-jp"
        item-key="fa_id"
        sort-by="autonomy_name_ruby"
        rowCount="-1"
        must-sort
      >
        <!-- 一覧内容 -->
        <template v-slot:[`item.municip_name`]="{ item }">
          {{ item.municip_name }}
        </template>
        <template v-slot:[`item.area_name`]="{ item }">
          {{ item.area_name }}
        </template>
        <template v-slot:[`item.autonomy_name`]="{ item }">
          {{ item.autonomy_name }}
        </template>
        <template v-slot:[`item.autonomy_name_ruby`]="{ item }">
          {{ item.autonomy_name_ruby }}
        </template>
        <template v-slot:[`item.population`]="{ item }">
          {{ item.population | format }}
        </template>
        <template v-slot:[`item.households_count`]="{ item }">
          {{ item.households_count }}世帯
        </template>
        <template v-slot:[`item.meeting_places`]="{ item }">
          <div v-html="getMeetingPlaceNames(item)"></div>
        </template>
        <template v-slot:[`item.shelter_places`]="{ item }">
          <div v-html="getShelterPlaceNames(item)"></div>
        </template>
        <template v-slot:[`item.actions`]="{ item }">
          <template v-if="readOnly">
            <v-btn small class="mx-2" color="primary" @click="handleView(item)"
              >参照</v-btn
            >
          </template>
          <template v-else>
            <v-btn small class="mx-2" color="primary" @click="handleEdit(item)"
              >編集</v-btn
            >
            <v-btn
              small
              class="mx-2"
              color="primary"
              @click="handleDelete(item)"
              >削除</v-btn
            >
          </template>
        </template>
      </top-operation-data-table>
    </v-sheet>
    <!-- ダイアログ -->
    <confirm-dialog ref="confirmDialog" />
    <ErrorDialog ref="errorDialog" />
    <!-- オーバーレイ -->
    <v-overlay :value="overlay" opacity="1" color="white">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>
  </div>
</template>

<script>
import Vue from "vue";
import cloneDeep from "lodash/cloneDeep";
import ConfirmDialog from "@/components/ConfirmDialog";
import ErrorDialog from "@/components/ErrorDialog";
import { RepositoryFactory } from "@/js/repository/RepositoryFactory";
import config from "@/js/const";
import Facility from "@/plugins/facility";
import FormUtils from "@/plugins/form-utils";
import { ScreenId } from "@/js/value-objects/screen-id";
import { FacilityClass } from "@/js/value-objects/facility-class";
import messageUtils from "@/mixins/message-utils";
import { AllAreaTranslator } from "@/js/translators/allAreaTranslator";
import Validation from "@/plugins/validation";
import { AutonomyTranslator } from "@/js/translators/autonomyTranslator";
import { fileDownload } from "@/js/utils";

Vue.use(Facility, FormUtils, Validation);

//自治会マスタリポジトリ
const autonomyRepository = RepositoryFactory.get("autonomy");

// DPND連携で未設定として扱う市町村コード
const DEFAULT_DPND_MUNICIP_CODE = "000000";

/**
 * バリデーションルールを作成する。
 * @function
 * @returns {Object} バリデーションルール
 */
const getRules = () => ({
  /**
   * 必須チェックルールを作成する
   * @function
   * @returns 必須チェックルール
   */
  required: () => v => !!v || "必須項目です。",

  /**
   * 数値チェックルールを作成する
   * @function
   * @param {boolean} [acceptNegative=false] 負数(マイナス記号)の入力を許容するかどうか
   * @param {booealn} [acceptFloat=false] 小数の入力を許容するかどうか
   * @returns 数値チェックルール
   */
  numeric: (
    acceptNegative = false,
    acceptFloat = false,
    pattern = new RegExp(
      `^${acceptNegative ? "-?" : ""}\\d+${acceptFloat ? "(\\.\\d+)?" : ""}$`
    )
  ) => v => !v || pattern.test(v) || "数値で入力してください"
});

/**
 * 一覧表のヘッダーを作成する。
 * @function
 * @returns 一覧表のヘッダー
 */
const getHeaders = () => {
  return [
    {
      text: "施設ID",
      value: "fa_id",
      align: " d-none"
    },
    {
      text: "市町名",
      value: "municip_name",
      align: "pre",
      class: "text-pre"
    },
    {
      text: "地域（地区）",
      value: "area_name",
      align: "pre",
      class: "text-pre"
    },
    {
      text: "自治会名",
      value: "autonomy_name"
    },
    {
      text: "自治会名（カナ）",
      value: "autonomy_name_ruby"
    },
    {
      text: "人口",
      value: "population"
    },
    {
      text: "世帯数",
      value: "households_count"
    },
    {
      text: "集合場所",
      value: "meeting_places",
      sortable: false
    },
    {
      text: "避難所",
      value: "shelter_places",
      sortable: false
    },
    {
      text: "操作",
      value: "actions",
      width: "14.5em",
      sortable: false
    }
  ];
};

/**
 * 空の集合場所情報を1つ保持する配列を作成する。
 * （CSVダウンロードの前処理）
 * @function
 * @returns 空の集合場所情報
 */
const createEmptyMeetingInfoForCSV = () => [
  {
    fa_id: "",
    fa_name: "",
    eva_municip_code: "",
    eva_municip_name: "",
    prevent_facls: "",
    prevent_facls_val: "",
    flagship_fa_flg: "",
    flagship_fa_flg_val: ""
  }
];

/**
 * 集合場所と避難所情報を結合し、両方が空の場合は空の情報を追加する。
 * （CSVダウンロードの前処理）
 * @function
 */
const addEmptyMeetingInfoIfNeeded = ({
  meeting_info = null,
  shelter_info = null,
  ...rest
}) => {
  const meetingArray =
    Array.isArray(meeting_info) && meeting_info.length > 0 ? meeting_info : [];
  const shelterArray =
    Array.isArray(shelter_info) && shelter_info.length > 0 ? shelter_info : [];
  const combinedInfo = [...meetingArray, ...shelterArray];

  return {
    ...rest,
    meeting_info:
      combinedInfo.length > 0 ? combinedInfo : createEmptyMeetingInfoForCSV()
  };
};

/**
 * 自治会情報×集合場所情報数分のレコードに平坦化する。
 * （CSVダウンロードの前処理）
 * @function
 */
const flatternMeetingInfoInfo = ({ meeting_info, ...rest }) =>
  meeting_info.map(meeting_info_elem => ({ ...rest, ...meeting_info_elem }));

const CSV_FIELDS = [
  { name: "自治会コード", attr: "autonomy_code" },
  { name: "自治会名", attr: "autonomy_name" },
  { name: "自治会名（カナ）", attr: "autonomy_name_ruby" },
  { name: "市町村コード", attr: "municip_code" },
  { name: "市町村名", attr: "municip_name" },
  { name: "地域コード", attr: "area_code" },
  { name: "地域名", attr: "area_name" },
  { name: "人口", attr: "population" },
  { name: "世帯数", attr: "households_count" },
  { name: "原子力防災施設区分ID", attr: "prevent_facls" },
  { name: "原子力防災施設区分", attr: "prevent_facls_val" },
  { name: "施設ID", attr: "fa_id" },
  { name: "施設名", attr: "fa_name" },
  { name: "施設市町村コード", attr: "eva_municip_code" },
  { name: "施設市町村名", attr: "eva_municip_name" },
  { name: "避難経路始点フラグ", attr: "flagship_fa_flg" },
  { name: "避難経路始点フラグ名称", attr: "flagship_fa_flg_val" }
];

/**
 * CSVの行を結合する関数を作成する。
 * @function
 * @param {string} [separator=\n] 行の区切り文字
 * @returns 区切り文字で行を結合する関数
 */
const getCSVRowJoiner = (separator = "\n") => lines =>
  `${lines.join(separator)}${separator}`;

/**
 * CSVの列を結合する関数を作成する。
 * @function
 * @param {string} [separator=,] 列の区切り文字
 * @param {string} [quote="] 値を囲う文字
 * @returns 区切り文字で列を結合する関数
 */
const getCSVColJoiner = (separator = ",", quote = '"') => fields =>
  fields.map(field => quote + field + quote).join(separator);

/**
 * 2次元配列をCSVテキストに変換する関数を作成する。
 * @function
 * @param {rowJoiner} 行結合関数
 * @param {colJoiner} 列結合関数
 */
const getCSVFormatter = (
  rowJoiner = getCSVRowJoiner(),
  colJoiner = getCSVColJoiner(),
  bom = "\ufeff"
) => records => bom + rowJoiner(records.map(colJoiner));

/**
 * CSV出力時の並び替え関数
 * 市町村コード、地域コード、自治会コードの昇順で並び替える。
 *
 * @function
 * @param {object} a 比較対象。APIの戻り値オブジェクト
 * @param {object} b 比較対象。APIの戻り値オブジェクト
 * @returns aの方が小さい場合は -1、大きい場合は 1、その他の場合は 0。
 */
const csvSorter = (a, b) => {
  if (a.municip_code === b.municip_code) {
    if (a.area_code === b.area_code) {
      if (a.autonomy_code === b.autonomy_code) {
        return 0;
      }
      return a.autonomy_code < b.autonomy_code ? -1 : 1;
    }
    return a.area_code < b.area_code ? -1 : 1;
  }
  return a.municip_code < b.municip_code ? -1 : 1;
};

/**
 * オブジェクトを配列に変換する関数を作成する。
 * @function
 * @param {string[]} order 配列が対応する属性名リスト
 * @returns オブジェクトを属性名リストにしたがって配列に変換する関数
 */
const toCSVColArray = (order = CSV_FIELDS.map(({ attr }) => attr)) => items =>
  order.map(key => items[key]);

/**
 * タイムスタンプ文字列(YYYYMMDD_HHMM)を作成する。
 * @param {Date} [date=new Date()] 文字列に変換対象の日付。
 * @returns タイムスタンプ文字列
 */
const getTimestamp = (date = new Date()) => {
  const padZero = value => value.toString().padStart(2, "0");

  const dateText = [date.getMonth() + 1, date.getDate()].map(padZero).join("");
  const timeText = [date.getHours(), date.getMinutes()].map(padZero).join("");

  return `${date.getFullYear()}${dateText}_${timeText}`;
};

/**
 * 編集可能な属性名のリストを作成する。
 * @function
 * @returns 属性名のリスト
 */
const getEditableAttributes = () => {
  return [
    "autonomy_name",
    "autonomy_name_ruby",
    "municip_code",
    "area_code",
    "population",
    "households_count",
    "meeting_info",
    "shelter_info"
  ];
};

/**
 * 空の集合場所を1つ保持する配列を作成する。
 * @function
 * @returns 空の集合場所情報
 * flg=1とすることで、空の時の避難経路始点の初期値として1行目が選択状態にする
 */
const createMeetingInfo = () => [
  {
    key: 1,
    eva_municip_code: "",
    fa_id: "",
    flagship_fa_flg: "1"
  }
];

/**
 * 空の避難所を1つ保持する配列を作成する。
 * @function
 * @returns 空の避難所情報
 */
const createShelterInfo = () => [
  {
    key: 1,
    eva_municip_code: "",
    fa_id: "",
    flagship_fa_flg: "0"
  }
];

/**
 * 空の編集アイテムを作成する。
 * @function
 * @returns 空の編集アイテム
 */
const createEditedItem = () => ({
  municip_code: "",
  area_code: "",
  autonomy_name: "",
  autonomy_name_ruby: "",
  population: "",
  households_count: "",
  meeting_info: [],
  edited_meeting_info: createMeetingInfo(),
  shelter_info: [],
  edited_shelter_info: createShelterInfo()
});

/**
 * 集合場所、避難所の編集対象（APIのCSV出力用の項目を含まない）項目リスト
 */
const filterInfoItems = [
  "key",
  "eva_municip_code",
  "fa_id",
  "flagship_fa_flg",
  "autonomy_code"
];

/**
 * APIのレスポンスを編集アイテムに変換する。
 * @function
 * @param {object} item APIのレスポンスオブジェクト
 * @param {object[]} item.eva_area_info 避難元所属地域情報
 * @returns {object} 編集アイテム
 */
const convertEditable = ({
  meeting_info = null,
  shelter_info = null,
  ...rest
}) => {
  const result = {
    // デフォルトアイテム
    ...createEditedItem(),
    // eva_area_info 以外を上書き
    ...rest
  };

  const filterInfo = info =>
    Object.fromEntries(filterInfoItems.map(field => [field, info[field]]));

  // meeting_info の設定
  if (Array.isArray(meeting_info) && meeting_info.length > 0) {
    const filteredMeetingInfo = meeting_info.map(filterInfo);
    result.meeting_info = cloneDeep(filteredMeetingInfo);
    result.edited_meeting_info = cloneDeep(filteredMeetingInfo);
  }
  // shelter_info の設定
  if (Array.isArray(shelter_info) && shelter_info.length > 0) {
    const filteredShelterInfo = shelter_info.map(filterInfo);
    result.shelter_info = cloneDeep(filteredShelterInfo);
    result.edited_shelter_info = cloneDeep(filteredShelterInfo);
  }

  // セレクトボックスに表示する値は、DPND連携の初期値を置き換える。
  // meeting_info 配列要素の置換対象 0→空にする
  const dpndMeetingInfoColumns = [
    ["eva_municip_code", DEFAULT_DPND_MUNICIP_CODE],
    ["fa_id", "0"]
  ];
  const dpndDefaultValueConverter = (targets, replace = "") => item => {
    for (const [name, value] of targets) {
      if (item[name] === value) {
        item[name] = replace;
      }
    }
  };
  const evaMeetingConverter = dpndDefaultValueConverter(dpndMeetingInfoColumns);
  result.meeting_info.forEach(evaMeetingConverter);
  result.edited_meeting_info.forEach(evaMeetingConverter);
  result.shelter_info.forEach(evaMeetingConverter);
  result.edited_shelter_info.forEach(evaMeetingConverter);

  return result;
};

/**
 * 原子力災害関係施設マスタ(集合場所,避難所)を取得する。
 * @param {string} screenId 画面ID
 * @param {string} [facilityClass=""] 施設区分
 */
const fetchFacilities = async (screenId, facilityClass = "") =>
  (await autonomyRepository.facilitiesList(screenId, facilityClass)).data
    .results;

/**
 * 自治会マスタを取得する。
 * @param {string} screenId 画面ID
 * @param {string} [municipCode=""] 市町村コード(省略時は全件)
 * @returns {object[]} 自治会リスト
 */
const fetchAutonomies = async (screenId, municipCode = "") =>
  (await autonomyRepository.list(screenId, municipCode)).data.results.map(
    element =>
      // facilityStore.loadFacility の実装を移植
      // 以下の属性を編集または追加する。
      // * eva_area_info: 要素を市町村コード、エリアコードでソート
      // * eva_area_info[].key: ソート後のインデックス+1を追加
      ({
        ...element,
        ...AutonomyTranslator.sortMeetingInfo(element.meeting_info)
      })
  );

/**
 * 登録または更新リクエスト用のペイロードを作成する
 * @function
 * @param {object} editedItem 編集アイテム
 * @returns {object} ペイロード
 */
const toCreateOrUpdatePayload = ({ ...item }) => {
  return {
    //自治会コード
    autonomy_code: item.autonomy_code,
    //市町村コード
    municip_code: item.municip_code,
    //地域コード
    area_code: item.area_code,
    //自治会名
    autonomy_name: item.autonomy_name,
    //自治会名フリガナ
    autonomy_name_ruby: item.autonomy_name_ruby,
    //人口
    population: item.population,
    //世帯数
    households_count: item.households_count,
    //編集済み集合場所情報
    meeting_info: item.edited_meeting_info || [],
    //編集済み避難所情報
    shelter_info: item.edited_shelter_info || [],
    //ハッシュ
    hash: item.hash
  };
};

export default {
  mixins: [messageUtils],

  components: {
    ConfirmDialog,
    ErrorDialog,
    ErrorMessage: () => import("@/components/ErrorMessage"),
    SuccessMessage: () => import("@/components/SuccessMessage"),
    MeetingInfo: () => import("@/components/autonomy/MeetingInfo"),
    ShelterInfo: () => import("@/components/autonomy/ShelterInfo"),
    TopOperationDataTable: () => import("@/components/ui/TopOperationDataTable")
  },

  data: () => ({
    config: config,
    overlay: false,
    loading: false,
    dialog: false,
    valid: false,
    screenTitle: "",
    screenId: "",
    readOnly: false,
    // 検索項目
    searchItem: {
      // 市町村
      municipality: "",
      // 地域
      area: "",
      // 人口（開始）
      population_low: "",
      // 人口（終了）
      population_high: "",
      // 世帯数（開始）
      households_count_low: "",
      // 世帯数（終了）
      households_count_high: "",
      // フリーテキスト
      freeText: ""
    },
    // 編集中の自治会マスタリストのインデックス(新規の場合は、-1)
    editedIndex: -1,
    // 編集中の自治会マスタ
    editedItem: createEditedItem(),
    // 施設リスト(リストボックス用)
    facilities_meeting: [],
    // 施設リスト(リストボックス用)
    facilities_shelter: [],
    //自治会リスト
    autonomys: []
  }),

  mounted() {
    this.pageTitle(this.screenTitle);
  },

  computed: {
    /**
     * フォームの検証ルール
     */
    rules() {
      return getRules();
    },

    /**
     * 一覧表のヘッダー
     */
    headers() {
      return getHeaders();
    },

    /**
     * 編集中のアイテムが新規登録かどうかを示す値
     */
    isNewEditedItem() {
      return this.editedIndex === -1;
    },

    /**
     * 表を表示するかどうかを示す値
     */
    showsDataTable() {
      return !!this.searchItem.municipality;
    },

    /**
     * フリーテキストの検索対象の属性名リスト
     */
    freeTextTargets() {
      return ["autonomy_name", "autonomy_name_ruby"];
    },

    /**
     * 検索項目によってフィルタした自治会アイテムリスト
     */
    filteredAutonomys() {
      // 常にTrue/Falseを返す関数
      const alwaysTrue = () => true;

      // 地区フィルタ
      const areaFilter = area =>
        area ? item => item.area_code === area : alwaysTrue;

      // 人口範囲フィルタ
      const populationFilter = (low, high) => {
        if (!low && !high) return alwaysTrue;
        return item => {
          const population = parseInt(item.population) || 0;
          const lowVal = low ? parseInt(low) : 0;
          const highVal = high ? parseInt(high) : Infinity;
          return population >= lowVal && population <= highVal;
        };
      };

      // 世帯数範囲フィルタ
      const householdsFilter = (low, high) => {
        if (!low && !high) return alwaysTrue;
        return item => {
          const households = parseInt(item.households_count) || 0;
          const lowVal = low ? parseInt(low) : 0;
          const highVal = high ? parseInt(high) : Infinity;
          return households >= lowVal && households <= highVal;
        };
      };

      // フリーテキストフィルタ（未指定の場合は常にTrue）
      const freeTextFilter = freeText =>
        freeText
          ? item =>
              this.freeTextTargets.some(name => item[name]?.includes(freeText))
          : alwaysTrue;

      // 各フィルタを適用
      return this.autonomys
        .filter(areaFilter(this.searchItem.area))
        .filter(
          populationFilter(
            this.searchItem.population_low,
            this.searchItem.population_high
          )
        )
        .filter(
          householdsFilter(
            this.searchItem.households_count_low,
            this.searchItem.households_count_high
          )
        )
        .filter(freeTextFilter(this.searchItem.freeText));
    },

    /**
     * 市町村のリスト
     */
    municipalities() {
      const municipalities = this.$store.state.allArea?.municipalities;
      if (municipalities && Array.isArray(municipalities)) {
        return municipalities;
      }

      return [];
    },

    /**
     * 市町村のリスト（一覧取得）
     */
    municipalitiesAddAll() {
      const municipalities = this.$store.state.allArea?.municipalities;
      if (municipalities && Array.isArray(municipalities)) {
        return [{ text: "すべて", value: "all" }, ...municipalities];
      }

      return [];
    },

    /**
     * 地域（地区）のリスト
     */
    areas() {
      const areas = this.$store.state.allArea?.areas;
      if (areas && Array.isArray(areas)) {
        return AllAreaTranslator.toAreasByMunicipality(
          this.$store.state.allArea?.areas ?? [],
          this.searchItem.municipality
        );
      }

      return [];
    },
    /**
     * 編集項目用地域（地区）のリスト
     * 編集項目で選択した自治体に基づき絞り込まれる
     */
    editedareas() {
      const editedareas = this.$store.state.allArea?.areas;
      if (editedareas && Array.isArray(editedareas)) {
        return AllAreaTranslator.toAreasByMunicipality(
          this.$store.state.allArea?.areas ?? [],
          this.editedItem.municip_code
        );
      }

      return [];
    },

    /**
     * デフォルトの編集項目属性名リスト
     */
    defaultEditableAttributes() {
      return getEditableAttributes();
    },

    /**
     * 指定された属性が編集対象がどうかを示す関数
     */
    isEditable() {
      return attributeName =>
        this.defaultEditableAttributes.includes(attributeName);
    },

    /**
     * フォームコントロール用の属性作成関数
     */
    formControlProps: () => {
      return function(
        placeholder = config.PLACEHOLDER_SP,
        counter = null,
        hint = null
      ) {
        const formControlProps = this.$formControlProps(
          this.readOnly,
          counter,
          true,
          hint
        );

        if (this.readOnly) {
          placeholder = config.PLACEHOLDER_SP;
        }

        formControlProps.placeholder = placeholder;
        formControlProps.readonly = formControlProps.disabled;
        delete formControlProps.disabled;

        return formControlProps;
      };
    }
  },

  watch: {
    $route: "initialize",

    /**
     * ダイアログの表示を監視
     */
    dialog(value) {
      if (value) {
        this.$nextTick(() => {
          // スクロールのリセット
          const elements = document.getElementsByClassName("v-dialog--active");
          if (elements && elements.length > 0) {
            elements[0].scrollTop = 0;
          }
          // バリデーションのリセット
          this.$refs.form?.resetValidation();
        });
      }
    },

    /**
     * 検索項目の変更を監視
     */
    searchItem: {
      handler() {
        this.resetPage();
      },
      deep: true
    },

    /**
     * 検索項目の市町村の変更を監視
     */
    "searchItem.municipality": async function() {
      this.overlay = true;
      try {
        this.searchItem.area = "";
        await this.refreshItems();
      } catch (e) {
        this.handleGetError(e);
      } finally {
        this.overlay = false;
      }
    }
  },

  created() {
    this.initialize();
  },

  methods: {
    /**
     * コンポーネントを初期化する。
     */
    async initialize() {
      this.overlay = true;
      try {
        // 操作権限
        const screen = this.$screenOperationAuthority(
          ScreenId.SF4001,
          ScreenId.SF4002
        );
        this.screenId = screen.screenId;
        this.readOnly = screen.readOnly;
        this.screenTitle = screen.screenTitle;

        // 市町村
        await this.$store.dispatch("allArea/loadAllArea");

        // リストボックス用施設名取得
        this.facilities_meeting = await fetchFacilities(
          this.screenId,
          FacilityClass.MEETING
        );
        this.facilities_shelter = await fetchFacilities(
          this.screenId,
          FacilityClass.SHELTER
        );
      } catch (error) {
        this.handleError(error, this.screenId, "aggregation-menu");
      } finally {
        this.overlay = false;
      }
    },

    /**
     * 選択中の市町村にしたがって、自治会マスタを取得する。
     */
    async refreshItems() {
      if (!this.searchItem.municipality) {
        this.autonomys = [];
      } else {
        //自治会マスタを取得
        this.autonomys = await fetchAutonomies(
          this.screenId,
          this.searchItem.municipality
        );
      }
    },

    /**
     * ダウンロードボタンのクリックイベントを処理する。
     */
    async handleDownload() {
      this.overlay = true;
      try {
        const records = (await fetchAutonomies(this.screenId))
          .map(addEmptyMeetingInfoIfNeeded)
          .flatMap(flatternMeetingInfoInfo);
        const sorted = [...records].sort(csvSorter);

        const matrix = [
          CSV_FIELDS.map(({ name }) => name),
          ...sorted.map(toCSVColArray())
        ];

        fileDownload(
          new Blob([getCSVFormatter()(matrix)], {
            type: "text/csv"
          }),
          `自治会マスタ_${getTimestamp()}.csv`
        );
      } finally {
        this.overlay = false;
      }
    },

    /**
     * 参照ボタンのクリックイベントを処理する。
     */
    handleView(item) {
      this.editedIndex = this.autonomys.indexOf(item);
      this.editedItem = convertEditable(item);
      this.openEditDialog();
    },

    /**
     * 編集ボタンのクリックイベントを処理する。
     */
    handleEdit(item) {
      // 編集中アイテムのインデックスを取得
      this.editedIndex = this.autonomys.indexOf(item);
      this.editedItem = convertEditable(item);
      this.openEditDialog();
    },

    /**
     * 削除ボタンのクリックイベントを処理する。
     */
    async handleDelete(item) {
      this.clearMessage();

      const messages = [
        "削除した場合には元に戻せません。本当によろしいですか？"
      ];
      for (let message of messages) {
        if (!(await this.showConfirmDialog("削除", message))) {
          return;
        }
      }

      this.overlay = true;
      try {
        // 自治会マスタを削除して再取得
        this.handleSuccess(await this.deleteRecord(item));
        await this.refreshItems();
      } catch (error) {
        this.handleUpdateError(error);
      } finally {
        this.overlay = false;
      }
    },

    /**
     * 編集ダイアログの登録または更新ボタンクリックイベントを処理する。
     */
    async handleSaveEditDialog() {
      // <v-form> に用意されているvalidateメソッドで入力項目チェック
      if (!this.$refs.form.validate()) {
        return;
      }
      if (this.$refs.meetingInfo && !this.$refs.meetingInfo.validate()) {
        // 集合場所の地図上の避難経路始点必須チェックエラーメッセージを表示
        const flagshipError = this.$refs.meetingInfo.getFlagshipValidationError();
        if (flagshipError) {
          this.showErrorDialog(flagshipError);
        }
        return;
      }
      if (this.$refs.shelterInfo && !this.$refs.shelterInfo.validate()) {
        return;
      }

      // ペイロード作成前に集合場所情報の代表フラグを正規化
      if (this.$refs.meetingInfo) {
        this.$refs.meetingInfo.prepareMeetingInfoForSave();
      }

      this.clearMessage();
      this.overlay = true;
      this.loading = true;
      try {
        // 自治会マスタを登録または更新して再取得
        // createRecordメソッドで実行
        if (this.isNewEditedItem) {
          this.handleSuccess(await this.createRecord());
        } else {
          this.handleSuccess(await this.updateRecord());
        }
        await this.refreshItems();
      } catch (e) {
        this.handleUpdateError(e);
      } finally {
        this.closeEditDialog();
        this.overlay = false;
        this.loading = false;
      }
    },

    /**
     * 編集ダイアログのキャンセルまたは戻るボタンクリックイベントを処理する。
     */
    handleCloseEditDialog() {
      this.closeEditDialog();
    },

    /**
     * 編集ダイアログを開く
     */
    openEditDialog() {
      this.dialog = true;

      this.$nextTick(() => {
        // スクロールのリセット
        const elements = document.getElementsByClassName("v-dialog--active");
        if (elements && elements.length > 0) {
          elements[0].scrollTop = 0;
        }
        // バリデーションのリセット
        this.$refs.form.resetValidation();
      });
    },

    /**
     * 編集ダイアログを閉じる
     */
    closeEditDialog() {
      this.dialog = false;
      this.$refs.form.resetValidation();

      this.$nextTick(() => {
        this.editedIndex = -1;
        this.editedItem = createEditedItem();

        //子コンポーネント初期化
        this.$nextTick(() => {
          if (this.$refs.meetingInfo) {
            this.$refs.meetingInfo.initialize();
          }
          if (this.$refs.shelterInfo) {
            this.$refs.shelterInfo.initialize();
          }
        });
      });
    },

    /**
     * 自治会マスタを登録する。
     */
    async createRecord() {
      return await autonomyRepository.create(
        this.screenId,
        toCreateOrUpdatePayload(this.editedItem)
      );
    },

    /**
     * 自治会マスタを更新する。
     */
    async updateRecord() {
      return await autonomyRepository.update(
        this.screenId,
        toCreateOrUpdatePayload(this.editedItem)
      );
    },

    /**
     * 自治会マスタを削除する。
     * @param {Object} item 削除するアイテム
     */
    async deleteRecord(item) {
      return await autonomyRepository.delete(this.screenId, {
        autonomy_code: item.autonomy_code,
        hash: item.hash
      });
    },

    /**
     * 確認ダイアログを表示する。
     * @param {string} buttonText ボタンに表示するテキスト
     * @param {...string} messages メッセージ
     * @returns {boolean} キャンセルされていないかどうかを示す値。
     */
    async showConfirmDialog(buttonText, ...messages) {
      return await this.$refs.confirmDialog.open(messages, buttonText);
    },

    /**
     * エラーダイアログを表示する。
     * @param {...string} messages メッセージ
     */
    showErrorDialog(...messages) {
      this.$refs.errorDialog.open(messages);
    },

    /**
     * 集合場所名を取得
     */
    getMeetingPlaceNames(item) {
      if (!item.meeting_info || !Array.isArray(item.meeting_info)) {
        return "";
      }

      const facilityNames = item.meeting_info
        .filter(meeting => meeting.fa_name)
        .map(meeting => meeting.fa_name)
        .filter(name => name);
      return facilityNames.join("<br>");
    },

    /**
     * 避難所名を取得
     */
    getShelterPlaceNames(item) {
      if (!item.shelter_info || !Array.isArray(item.shelter_info)) {
        return "";
      }

      const facilityNames = item.shelter_info
        .filter(shelter => shelter.fa_name)
        .map(shelter => shelter.fa_name)
        .filter(name => name);
      return facilityNames.join("<br>");
    },

    /**
     * 一覧表のページネーションをリセット
     */
    resetPage() {
      this.$refs.table?.resetPage();
    }
  }
};
</script>
